import { DataResult, State } from "@progress/kendo-data-query";
import { DatePickerPropsContext } from "@progress/kendo-react-dateinputs";
import {
  GridDataStateChangeEvent,
  GridItemChangeEvent,
  GridRowProps,
  GridToolbar,
} from "@progress/kendo-react-grid";
import { NumericTextBoxPropsContext } from "@progress/kendo-react-inputs";
import * as React from "react";
import { ReactElement, useEffect, useRef, useState } from "react";
import { useTheme } from "@mui/material";
import { GridColumn } from "@progress/kendo-react-grid/dist/npm/GridColumn";
import StyledKendoGrid from "./StyledKendoGrid";
import { GridDataLoader } from "./GridDataLoader";
import { GridResult, toGridResult } from "./GridResult";
import { InlineEditRowRender } from "./InlineEdit/InlineEditRowRender";
import { MdmGridProps } from "./MdmGridProps";
import { GRID_SELECTED_FIELD } from "./Selectable/GridSelection";
import { InitialState } from "./InitialDataState";
import { useDefaultDatePickerProps } from "./useDefaultDatePickerProps";
import { useDefaultNumericTextBoxProps } from "./useDefaultNumericTextBoxProps";
import StatePersistenceButtons from "./StatePersistenceButtons";
import useSavedState from "./UseSavedState";
import { isColumnFilterActive } from "./Extensions/FilterStatusHelper";

type MdmKendoGridProps<TData, TGridItem> = {
  mapItem: (item: TData) => TGridItem;
  onDataStateChanged: (event: GridDataStateChangeEvent) => void;
  onRowClick: (item: TData) => void;
  toolbarItems?: JSX.Element[];
  selection?: TData[];
  localStorageKey: string;
  refetchedData?: DataResult;
};

export default function MdmKendoGrid<TData, TGridItem, TExport>({
  fetchUrl,
  mapItem,
  localStorageKey,
  idSelector,
  onDataStateChanged,
  columns,
  toolbarItems,
  customToolbarItems,
  onRowClick,
  selection = [],
  onRowDoubleClick = () => undefined,
  initialSorting,
  pageSizes = [25, 50, 75, 100],
  buttonCount = 5,
  hideInlineFilterOperator = false,
  onInlineEditFinished = () => undefined,
  refetchedData,
  handleDataState,
}: MdmKendoGridProps<TData, TGridItem> & MdmGridProps<TData, TExport>) {
  /**
   * Data
   */
  const [data, setData] = useState<GridResult<TData>>({
    result: [],
    count: 0,
  });

  const processResult = (dataResult: DataResult) =>
    setData(toGridResult<TData>(dataResult.data.map(mapItem), dataResult.total));

  const [dataState, setDataState] = useSavedState<State>(localStorageKey, {
    ...InitialState,
    sort: initialSorting ? [initialSorting] : [],
  });

  useEffect(() => {
    const dataHasChanged =
      refetchedData && refetchedData.data.length > 0 && refetchedData?.data !== data.result;

    if (dataHasChanged) {
      setData(toGridResult(refetchedData.data, refetchedData?.total));
    }
  }, [refetchedData, data.result]);

  /**
   * Inline Edit
   */
  const onItemChange = (event: GridItemChangeEvent) => {
    const editItemID = idSelector(event.dataItem);
    const editedField = event.field;
    const newData = data.result.map((item) =>
      idSelector(item) === editItemID ? { ...item, [editedField || ""]: event.value } : item
    );
    setData({ result: newData, count: data.count });
  };

  const rowRender = (trElement: ReactElement<HTMLTableRowElement>, props: GridRowProps) => {
    return (
      <InlineEditRowRender
        originalProps={props}
        tr={trElement}
        editField={GRID_SELECTED_FIELD}
        exitEdit={onInlineEditFinished}
      />
    );
  };

  /**
   * Grid
   */
  const datePickerProps = useDefaultDatePickerProps();
  const numericTextBoxProps = useDefaultNumericTextBoxProps();
  const gridRef = useRef<any>();
  const theme = useTheme();

  return (
    <>
      <DatePickerPropsContext.Provider value={datePickerProps}>
        <NumericTextBoxPropsContext.Provider value={numericTextBoxProps}>
          <StyledKendoGrid
            rowRender={rowRender}
            editField={GRID_SELECTED_FIELD}
            onItemChange={(event) => onItemChange(event)}
            height="75vh"
            ref={gridRef}
            muiTheme={theme}
            hideInlineFilterOperator={hideInlineFilterOperator}
            sortable
            filterable
            scrollable="scrollable"
            resizable
            pageable={{
              pageSizes,
              buttonCount,
            }}
            selectedField={GRID_SELECTED_FIELD}
            onRowClick={(e) => onRowClick(e.dataItem as TData)}
            onRowDoubleClick={(e) => onRowDoubleClick(e.dataItem as TData)}
            data={{
              data: data.result.map(mapItem),
              total: data.count,
            }}
            {...dataState}
            onDataStateChange={(e: GridDataStateChangeEvent) => {
              onDataStateChanged(e);
              setDataState(e.dataState);
              if (handleDataState) handleDataState(e.dataState);
            }}
          >
            <GridToolbar>
              <StatePersistenceButtons
                localStorageKey={localStorageKey}
                initialState={InitialState}
                dataState={dataState}
                resetButtonText="RESET FILTER"
                saveButtonText="SAVE FILTER"
                align="left"
              />
              {toolbarItems}
              {customToolbarItems?.map((it) => it(dataState, gridRef, selection))}
            </GridToolbar>
            {columns.map((column) => (
              <GridColumn
                key={column.key}
                {...column.props}
                headerClassName={
                  isColumnFilterActive(column.props.field, dataState) ? "filterActive" : ""
                }
              />
            ))}
          </StyledKendoGrid>
        </NumericTextBoxPropsContext.Provider>
      </DatePickerPropsContext.Provider>
      <GridDataLoader
        url={fetchUrl}
        dataState={dataState}
        onDataReceived={(dataResult) => processResult(dataResult)}
      />
    </>
  );
}
